import { useEditField } from '@atlassian/jira-issue-field-base/src/services/edit-field-service/main.tsx';
import { TEXT_AREA_CF_TYPE } from '@atlassian/jira-platform-field-config';
import { createClipboardHelperComponent } from '../../../clipboard-handler/create-clipboard-helper-component';
import { useClipboardHandler } from '../../../clipboard-handler/use-clipboard-handler';
import {
	assertClipboardDataIsCompatible,
	assertClipboardHasPlainTextFormat,
} from '../../../clipboard-handler/utils';
import type {
	ClipboardItem,
	ListIssueFieldTransformed,
	ListIssueForCopyPaste,
	PasteHandler,
	PasteMetadata,
} from '../../../types';
import { richTextSupportedTypes } from '../supported-field-types';
import type { TransformedRichTextField } from '../types';

const getPastedValue = (
	clipboardItem: ClipboardItem,
	metadata: PasteMetadata | null,
): string | null => {
	if (
		!assertClipboardHasPlainTextFormat(clipboardItem) ||
		!assertClipboardDataIsCompatible(metadata, richTextSupportedTypes)
	) {
		return null;
	}

	return JSON.parse(clipboardItem.plainText);
};

function assertRichTextField(
	field: ListIssueFieldTransformed,
): asserts field is TransformedRichTextField {
	if (!field) {
		throw new Error('Field is required');
	}

	if (field.type !== TEXT_AREA_CF_TYPE) {
		throw new Error('Field type is not supported');
	}
}

const useRichTextClipboardHandler: PasteHandler = (
	issue: ListIssueForCopyPaste,
	field: ListIssueFieldTransformed,
) => {
	assertRichTextField(field);

	const [{ value }, { saveValue }] = useEditField<string | null, null>({
		issueKey: issue.key,
		fieldKey: field.fieldId,
		fieldType: field.type,
		initialValue: field.value ?? null,
	});

	return useClipboardHandler({
		issue,
		field,
		currentStringValue: typeof value !== 'string' ? JSON.stringify(value) : '',
		currentValue: value,
		getPastedValue,
		saveValue,
	});
};

const RichTextClipboardHelper = createClipboardHelperComponent(useRichTextClipboardHandler);

export const richTextClipboardHandler = {
	[TEXT_AREA_CF_TYPE]: RichTextClipboardHelper,
};
